<script lang="ts">
import Vue from 'vue'
import {Component, Prop, Watch} from 'vue-property-decorator'
import {capitalize, groupBy, sortBy} from 'lodash'

import api from '@/api'
import {ApexOptions} from 'apexcharts'

@Component({})
export default class SpaceChartNightTimeActivity extends Vue {
  @Prop()
  spaceId: string

  interval = '7d'
  groupByTime = 'HOUR'
  timeOfDay = 'NIGHT'
  daysOfWeek = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY']
  chartType: any = {type: 'bar', stacked: true}

  promise: Promise<any> = null
  events: any[] = []

  get chartOptions(): ApexOptions {
    return {
      chart: this.chartType,
      yaxis: [
        {
          title: {
            text: 'Total Motion Count',
          },
        },
      ],
      xaxis: {
        categories: this.daysOfWeek,
        labels: {
          formatter: (val) => capitalize(val),
        },
        tickPlacement: 'between',
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          horizontal: false,
        },
      },
      legend: {
        showForSingleSeries: true,
      },
    }
  }

  get series(): any[] {
    /**
     *    {
     *         "uid": "000097CD3E056460000099CD3E056460",
     *         "deviceType": "BASE_STATION",
     *         "eventType": "MOTION_COUNT",
     *         "locationId": "800",
     *         "locationName": "Office",
     *         "value": 10,
     *         "timeOfDay": "MORNING",
     *         "dayOfWeek": "THURSDAY"
     *     }
     */
    let grouped = groupBy(this.events, 'locationName')
    let ret = []

    for (let locationName in grouped) {
      let subGroup = groupBy(grouped[locationName], 'dayOfWeek')
      let subGroupData = []

      for (let dayOfWeek in subGroup) {
        let dayTotal = subGroup[dayOfWeek].reduce((total, event) => total + event.value, 0)
        subGroupData.push({
          x: dayOfWeek,
          y: dayTotal,
          z: Math.max(20, dayTotal),
        })
      }

      subGroupData = sortBy(subGroupData, (series) => this.daysOfWeek.indexOf(series.x))

      ret.push({
        name: locationName,
        data: subGroupData,
      })
    }
    return ret
  }

  setTimeframe(interval: string, groupByTime: any) {
    this.events = []
    this.interval = interval
    this.groupByTime = groupByTime

    this.promise = api.spaces
      .chartRoomActivityGrouping(this.spaceId, {
        interval: interval,
        group_by_time: groupByTime,
      })
      .then((resp) => {
        this.events = resp.data.events.filter((event: any) => {
          return event['timeOfDay'] == this.timeOfDay
        })
      })
  }

  setChartType(settings: any) {
    this.chartType = settings
  }

  mounted() {
    this.setTimeframe(this.interval, this.groupByTime)
  }
}
</script>
|
<template>
  <div>
    <h4 class="mt-5 mb-3">
      Night Time Activity Scatter Plot ({{ interval }})

      <span class="small">
        <b-link @click="setChartType({type: 'bar', stacked: true})" class="ml-1 small">Bar</b-link>
        &centerdot;
        <b-link @click="setChartType({type: 'heatmap'})" class="ml-1 small">Heatmap</b-link>
        &centerdot;
        <b-link @click="setChartType({type: 'bubble'})" class="ml-1 small">Scatter Plot</b-link>
      </span>
    </h4>

    <div class="row mt-4">
      <div class="col">
        <div class="card card-body">
          <promised :promise="promise" :pending-delay="0">
            <template #pending>
              <div class="spinner-border mx-auto" role="status"></div>
            </template>
            <template #rejected>
              <div class="card-subtitle text-danger mt-2">Error loading data</div>
            </template>
            <template>
              <div v-if="events.length">
                <div>
                  <apexchart
                    v-if="events"
                    height="400"
                    :options="chartOptions"
                    :series="series"></apexchart>
                </div>
              </div>
              <div v-else>
                <div class="card-subtitle text-muted mt-2">No data</div>
              </div>
            </template>
          </promised>
        </div>
      </div>
    </div>
  </div>
</template>
